import React, { useMemo, useState } from "react";
import { errorMessage } from "../../services";
import { createCombo, updateCombo } from "../../services/products";
import { money } from "../../utils/format";
import { Spinner } from "../../components/Loader";

const EMPTY = { name: "", price: "", offerTitle: "Combo pack", offerNote: "", items: [{ id: "", qty: 1 }, { id: "", qty: 1 }] };
const isCombo = (p) => Array.isArray(p.comboItems) && p.comboItems.length > 0;

// Website editor -> Offers: make "buy these together" combo packs at a special price. A combo is saved at once
// (it is its own product) and shows in the Offers section; its stock comes from the products in it.
// onSaved(product) adds it to the editor's draft, so unsaved edits elsewhere are kept.
export default function ComboBuilder({ siteId, products, notify, onSaved }) {
  const [form, setForm] = useState(null); // null = closed; { ...EMPTY, editId? } = open
  const [busy, setBusy] = useState(false);
  const combos = products.filter(isCombo);
  const choosable = products.filter((p) => typeof p.id === "number" && !isCombo(p));
  const byId = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  const chosen = form ? form.items.filter((i) => byId.has(Number(i.id))) : [];
  const fullPrice = chosen.reduce((sum, i) => sum + byId.get(Number(i.id)).price * (Number(i.qty) || 1), 0);
  const price = Number(form && form.price) || 0;
  const saving = fullPrice - price;

  const open = (combo) => setForm(combo
    ? { editId: combo.id, name: combo.name, price: combo.price, offerTitle: combo.offerTitle || "Combo pack", offerNote: combo.offerNote || "", items: combo.comboItems.map((c) => ({ id: c.id, qty: c.qty })) }
    : EMPTY);
  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setItem = (index, key, value) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === index ? { ...it, [key]: value } : it)) }));

  const save = async () => {
    if (!form.name.trim()) return notify("Give the combo a name.", "warn");
    if (chosen.length < 2) return notify("Choose at least 2 products.", "warn");
    if (!price) return notify("Set the combo price.", "warn");
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), price, offerTitle: form.offerTitle, offerNote: form.offerNote, items: chosen.map((i) => ({ id: Number(i.id), qty: Number(i.qty) || 1 })) };
      const { data } = form.editId ? await updateCombo(siteId, form.editId, payload) : await createCombo(siteId, payload);
      notify(data.message);
      setForm(null);
      await onSaved(data.product);
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="combo-builder">
      <div className="combo-builder-head">
        <div><b>🎁 Combo packs</b><small>Sell products together at a special price, e.g. "Plant + pot + soil".</small></div>
        {!form && <button type="button" className="primary-btn" onClick={() => open(null)}>＋ Create combo</button>}
      </div>

      {combos.length > 0 && !form && (
        <ul className="combo-list">
          {combos.map((c) => (
            <li key={c.id}>
              <img src={c.img} alt="" />
              <div>
                <b>{c.name}</b>
                <small>{c.comboItems.map((i) => `${i.qty > 1 ? `${i.qty}× ` : ""}${(byId.get(i.id) || {}).name || "Removed product"}`).join(" + ")}</small>
              </div>
              <span className="combo-price"><b>{money(c.price)}</b><del>{money(c.originalPrice)}</del></span>
              <button type="button" className="outline-btn" onClick={() => open(c)}>Edit</button>
            </li>
          ))}
        </ul>
      )}

      {form && (
        <div className="combo-form">
          <label className="admin-field">Combo name<input value={form.name} maxLength={150} onChange={setField("name")} placeholder="e.g. Balcony Starter Combo" /></label>
          <div className="combo-items-label">Products in this combo</div>
          {form.items.map((item, index) => (
            <div className="combo-item-row" key={index}>
              <select value={item.id} onChange={(e) => setItem(index, "id", e.target.value)} aria-label={`Product ${index + 1}`}>
                <option value="">Choose a product…</option>
                {choosable.map((p) => <option key={p.id} value={p.id}>{p.name} · {money(p.price)}</option>)}
              </select>
              <input type="number" min="1" max="20" value={item.qty} onChange={(e) => setItem(index, "qty", e.target.value)} aria-label="Quantity" />
              {form.items.length > 2 && <button type="button" className="danger-text-btn" onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== index) }))}>Remove</button>}
            </div>
          ))}
          {form.items.length < 10 && <button type="button" className="outline-btn" onClick={() => setForm((f) => ({ ...f, items: [...f.items, { id: "", qty: 1 }] }))}>＋ Add product</button>}
          <div className="form-row combo-price-row">
            <label className="admin-field">Combo price (₹)<input type="number" min="1" value={form.price} onChange={setField("price")} /></label>
            <div className="combo-summary">
              <span>Bought separately <b>{money(fullPrice)}</b></span>
              {price > 0 && fullPrice > 0 && (saving > 0
                ? <span className="good">Customers save <b>{money(saving)}</b> ({Math.round((saving / fullPrice) * 100)}%)</span>
                : <span className="bad">The combo costs more than buying separately</span>)}
            </div>
          </div>
          <div className="form-row">
            <label className="admin-field">Offer title <em className="muted">optional</em><input value={form.offerTitle} maxLength={40} onChange={setField("offerTitle")} /></label>
            <label className="admin-field">Offer description <em className="muted">optional</em><input value={form.offerNote} maxLength={120} onChange={setField("offerNote")} placeholder="e.g. Everything to start a balcony garden" /></label>
          </div>
          <div className="product-modal-actions">
            <button type="button" className="outline-btn" onClick={() => setForm(null)} disabled={busy}>Cancel</button>
            <button type="button" className="primary-btn" onClick={save} disabled={busy}>{busy ? <><Spinner /> Saving</> : form.editId ? "Save combo" : "Create combo"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
